import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { realtimeService } from "../services/firebase";
import type { Order } from "../store/slices/ordersSlice";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { Package, Truck, CheckCircle, Clock, XCircle, ChevronLeft } from "lucide-react";

const statusSteps = [
  { key: "pending", label: "Pending", icon: <Clock className="w-5 h-5" /> },
  { key: "confirmed", label: "Confirmed", icon: <CheckCircle className="w-5 h-5" /> },
  { key: "shipped", label: "Shipped", icon: <Truck className="w-5 h-5" /> },
  { key: "delivered", label: "Delivered", icon: <CheckCircle className="w-5 h-5" /> },
  { key: "cancelled", label: "Cancelled", icon: <XCircle className="w-5 h-5" /> },
];

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    const unsubscribe = realtimeService.onOrdersChange((orders) => {
      const found = orders.find((o) => o.id === orderId);
      setOrder(found || null);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order not found</h2>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Find the current step index
  const stepIndex = statusSteps.findIndex((step) => step.key === order.status);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-md p-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center mb-6 text-primary-600 hover:underline"
        >
          <ChevronLeft className="w-5 h-5 mr-1" /> Back to Orders
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Order #{order.id}</h1>
        <p className="text-gray-600 mb-6">
          Placed on {(() => {
            if (typeof order.createdAt === "string") {
              return new Date(order.createdAt).toLocaleDateString();
            } else if (order.createdAt && typeof order.createdAt === "object" && "seconds" in order.createdAt) {
              // @ts-expect-error Firestore Timestamp object may have 'seconds' property
              return new Date(order.createdAt.seconds * 1000).toLocaleDateString();
            }
            return "N/A";
          })()}
        </p>

        {/* Status Stepper */}
        <div className="flex items-center justify-between mb-8">
          {statusSteps.map((step, idx) => {
            const isActive = idx <= stepIndex && order.status !== "cancelled";
            const isCancelled = order.status === "cancelled" && step.key === "cancelled";
            return (
              <div key={step.key} className="flex-1 flex flex-col items-center">
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-full border-2 mb-2 ${
                    isActive || isCancelled
                      ? isCancelled
                        ? "border-red-500 bg-red-100 text-red-600"
                        : "border-primary-600 bg-primary-100 text-primary-600"
                      : "border-gray-300 bg-gray-100 text-gray-400"
                  }`}
                >
                  {step.icon}
                </div>
                <span
                  className={`text-xs font-medium ${
                    isActive || isCancelled
                      ? isCancelled
                        ? "text-red-600"
                        : "text-primary-600"
                      : "text-gray-400"
                  }`}
                >
                  {step.label}
                </span>
                {idx < statusSteps.length - 1 && (
                  <div
                    className={`w-full h-1 mt-1 ${
                      idx < stepIndex && order.status !== "cancelled"
                        ? "bg-primary-600"
                        : "bg-gray-200"
                    }`}
                  ></div>
                )}
              </div>
            );
          })}
        </div>

        {/* Order Items */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h2>
          <div className="space-y-4">
            {order.items.map((item) => (
              <div key={item.id} className="flex items-center space-x-4">
                <img
                  src={item.image || "/placeholder.svg"}
                  alt={item.name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{item.name}</h4>
                  <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                </div>
                <p className="font-semibold text-gray-900">₹{(item.price * item.quantity).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Total Amount</span>
            <span className="text-xl font-bold text-primary-600">₹{order.total.toLocaleString()}</span>
          </div>
        </div>

        {/* Shipping Address */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping Address</h2>
          <div className="text-gray-700">
            <div>{order.shippingAddress?.name}</div>
            <div>{order.shippingAddress?.address}</div>
            <div>
              {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.pincode}
            </div>
            <div>Phone: {order.shippingAddress?.phone}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail; 